const API_BASE = 'https://quoto-api.danialdldldl.workers.dev/api';

async function request<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
}

interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
}

interface PaginatedResponse<T = any> {
  success: boolean;
  data: T[];
  total: number;
  page: number;
  totalPages: number;
}

export const api = {
  quotes: {
    getToday: () => request<ApiResponse>('/today'),
    getRandom: () => request<ApiResponse>('/random'),
    getAll: (params?: Record<string, string>): Promise<PaginatedResponse> => {
      const query = params ? '?' + new URLSearchParams(params).toString() : '';
      return request(`/quotes${query}`);
    },
    getById: (id: number): Promise<ApiResponse> => request(`/quote/${id}`),
  },
  search: (params: Record<string, string>): Promise<PaginatedResponse> => {
    const query = new URLSearchParams(params).toString();
    return request(`/search?${query}`);
  },
  authors: {
    getAll: (): Promise<ApiResponse> => request('/authors'),
    getBySlug: (slug: string): Promise<ApiResponse> => request(`/author/${slug}`),
  },
  categories: {
    getAll: (): Promise<ApiResponse> => request('/categories'),
    getBySlug: (slug: string): Promise<ApiResponse> => request(`/category/${slug}`),
  },
  auth: {
    login: (username: string, password: string): Promise<ApiResponse> =>
      request('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
    logout: (): Promise<ApiResponse> => request('/auth/logout', { method: 'POST' }),
  },
  admin: {
    getStats: (): Promise<ApiResponse> => request('/admin/stats'),
    getQuotes: (): Promise<ApiResponse> => request('/admin/quotes'),
    createQuote: (data: any): Promise<ApiResponse> => request('/admin/quotes', { method: 'POST', body: JSON.stringify(data) }),
    updateQuote: (id: number, data: any): Promise<ApiResponse> =>
      request(`/admin/quotes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    deleteQuote: (id: number): Promise<ApiResponse> => request(`/admin/quotes/${id}`, { method: 'DELETE' }),
    bulkDelete: (ids: number[]): Promise<ApiResponse> =>
      request('/admin/quotes/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }),
    importQuotes: (quotes: any[]): Promise<ApiResponse> =>
      request('/admin/quotes/import', { method: 'POST', body: JSON.stringify({ quotes }) }),
    exportQuotes: (): Promise<ApiResponse> => request('/admin/quotes/export'),
  },
  settings: {
    get: (): Promise<ApiResponse> => request('/settings'),
    update: (settings: Record<string, string>): Promise<ApiResponse> =>
      request('/settings', { method: 'PUT', body: JSON.stringify(settings) }),
  },
  favorites: {
    getAll: (sessionId: string): Promise<ApiResponse> =>
      request('/favorites', { headers: { 'X-Session-Id': sessionId } }),
    add: (quoteId: number, sessionId: string): Promise<ApiResponse> =>
      request(`/favorites/${quoteId}`, {
        method: 'POST',
        headers: { 'X-Session-Id': sessionId },
      }),
    remove: (quoteId: number, sessionId: string): Promise<ApiResponse> =>
      request(`/favorites/${quoteId}`, {
        method: 'DELETE',
        headers: { 'X-Session-Id': sessionId },
      }),
  },
  telegram: {
    getSettings: (): Promise<ApiResponse> => request('/telegram/settings'),
    updateSettings: (settings: any): Promise<ApiResponse> =>
      request('/telegram/settings', { method: 'PUT', body: JSON.stringify(settings) }),
    testConnection: (): Promise<ApiResponse> => request('/telegram/test', { method: 'POST' }),
  },
};

export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('quoto_token');
}

export function setToken(token: string) {
  localStorage.setItem('quoto_token', token);
}

export function removeToken() {
  localStorage.removeItem('quoto_token');
}

export function getSessionId(): string {
  if (typeof window === 'undefined') return '';
  let sessionId = localStorage.getItem('quoto_session');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem('quoto_session', sessionId);
  }
  return sessionId;
}
